import type { Schema } from '@cli-types/core-engine/schema.js'
import type { DevModeSnippet, DevModeSnippetCodeTab } from '@cli-types/next-adapter/generators.js'
import { ENTITY_ACTION_FIELD_EXCLUSIONS, SAMPLE_UUID } from '@cli-constants/generators.js'
import {
  pluralize,
  singularize,
  singularizeLabel,
  toCamelCase,
  toPascalCase
} from '@cli-utils/text.js'
import { getEntityFieldContext } from '@next-adapter/generators/entity-filters/get-entity-field-context.js'
import { renderBuildEntityDevModeSnippetsRenderer } from '@next-adapter/templates/renderers/generators/dev-mode-integration/build-entity-dev-mode-snippets.js'
import { createEntityDataObject } from './create-entity-data-object.js'
import { createInputObject } from './create-input-object.js'
import { createPayloadExample } from './create-payload-example.js'
import { createResponseExample } from './create-response-example.js'
import { formatTsObject } from './format-ts-object.js'
import { isActionField } from './is-action-field.js'
import { resolveActionModulePath } from './resolve-action-module-path.js'
export function buildEntityDevModeSnippets(
  schema: Schema,
  actionImportPath: string,
  adminImportAlias?: string
): DevModeSnippet[] {
  const singular = singularize(schema.name)
  const plural = pluralize(schema.name)
  const Singular = toPascalCase(singular)
  const Plural = toPascalCase(plural)
  const camelSingular = toCamelCase(singular)
  const camelPlural = toCamelCase(plural)
  const entityLabel = singularizeLabel(schema.label)
  const { regularDbFields } = getEntityFieldContext(schema)
  const inputFields = regularDbFields.filter((field) =>
    isActionField(field, ENTITY_ACTION_FIELD_EXCLUSIONS)
  )
  const hasSlugField = regularDbFields.some((field) => field.name === 'slug')
  const createInput: Record<string, unknown> = createInputObject(inputFields)
  const updateInput: Record<string, unknown> = { id: SAMPLE_UUID, ...createInput }
  const actionModulePath = resolveActionModulePath(actionImportPath, adminImportAlias)
  const detailByIdCode = renderBuildEntityDevModeSnippetsRenderer({
    kind: 'renderBuildEntityDevModeSnippetsImportsTemplate',
    props: {
      singular: Singular,
      actionModulePath: actionModulePath,
      camelSingular: camelSingular
    }
  })
  const detailBySlugCode = renderBuildEntityDevModeSnippetsRenderer({
    kind: 'renderBuildEntityDevModeSnippetsImportsDetailsTemplate',
    props: {
      singular: Singular,
      actionModulePath: actionModulePath,
      camelSingular: camelSingular
    }
  })
  const detailCodeTabs: DevModeSnippetCodeTab[] | undefined = hasSlugField
    ? [
        { id: 'by-id', label: 'By ID', language: 'tsx', code: detailByIdCode },
        { id: 'by-slug', label: 'By Slug', language: 'tsx', code: detailBySlugCode }
      ]
    : undefined

  if (schema.actions?.draft === true) {
    createInput.published = true
    updateInput.published = true
  }

  const entityData = createEntityDataObject(schema)
  const listResponse = { [camelPlural]: [entityData], total: 1 }
  const createResponse = { success: true, [camelSingular]: entityData }
  const deleteResponse = { success: true }

  return [
    {
      id: 'list',
      label: `${schema.label} List`,
      kind: 'query',
      icon: 'list',
      language: 'tsx',
      responseExample: createResponseExample(listResponse),
      code: renderBuildEntityDevModeSnippetsRenderer({
        kind: 'renderBuildEntityDevModeSnippetsImportsLabelTemplate',
        props: {
          plural: Plural,
          actionModulePath: actionModulePath,
          camelPlural: camelPlural,
          toLowerCase: schema.label.toLowerCase()
        }
      })
    },
    {
      id: 'detail',
      label: `${entityLabel} Detail`,
      kind: 'query',
      icon: 'eye',
      language: 'tsx',
      responseExample: createResponseExample(entityData),
      code: detailByIdCode,
      codeTabs: detailCodeTabs
    },
    {
      id: 'create',
      label: `Create ${entityLabel}`,
      kind: 'mutation',
      icon: 'plus',
      language: 'tsx',
      payloadExample: createPayloadExample(createInput),
      responseExample: createResponseExample(createResponse),
      code: renderBuildEntityDevModeSnippetsRenderer({
        kind: 'renderBuildEntityDevModeSnippetsClientModuleTemplate',
        props: {
          singular: Singular,
          actionModulePath: actionModulePath,
          tsObject: formatTsObject(createInput),
          toLowerCase: entityLabel.toLowerCase()
        }
      })
    },
    {
      id: 'update',
      label: `Update ${entityLabel}`,
      kind: 'mutation',
      icon: 'pencil',
      language: 'tsx',
      payloadExample: createPayloadExample(updateInput),
      responseExample: createResponseExample(createResponse),
      code: renderBuildEntityDevModeSnippetsRenderer({
        kind: 'renderBuildEntityDevModeSnippetsClientModuleSampleTemplate',
        props: {
          singular: Singular,
          actionModulePath: actionModulePath,
          tsObject: formatTsObject(updateInput).replace(`id: "${SAMPLE_UUID}"`, 'id'),
          toLowerCase: entityLabel.toLowerCase()
        }
      })
    },
    {
      id: 'delete',
      label: `Delete ${entityLabel}`,
      kind: 'mutation',
      icon: 'trash',
      language: 'tsx',
      payloadExample: createPayloadExample(SAMPLE_UUID),
      responseExample: createResponseExample(deleteResponse),
      code: renderBuildEntityDevModeSnippetsRenderer({
        kind: 'renderBuildEntityDevModeSnippetsClientModuleDetailsTemplate',
        props: {
          singular: Singular,
          actionModulePath: actionModulePath,
          toLowerCase: entityLabel.toLowerCase()
        }
      })
    }
  ]
}
